/**
 * Now / Timeliness component.
 *
 * "Of the KPI instances reporting, how many had their LATEST entry submitted
 *  within the grace window?"
 *
 * Score = (on_time_instances / reported_instances) * 100
 *
 * DEV-851 (per-instance refactor): Granularity moved from per-entry to
 * per-instance. The previous per-entry rule produced a sticky score —
 * historical late entries permanently dragged it down because editing an
 * existing row doesn't change `created_at`. Per-instance uses ONLY the
 * latest entry per instance, so adding fresh on-time data shifts the
 * score immediately.
 *
 * Rule per instance:
 *   on_time = (latest_created_at - latest_reporting_date) <= grace_days
 *
 * Instances that haven't been reported at all are excluded from the
 * Timeliness denominator (Completeness handles "was it reported?").
 *
 * See: DEV-846 epic, DEV-847, DEV-851 (PR2).
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

/**
 * Days between two ISO strings (date or full ISO timestamp). Uses Date.parse
 * which handles both YYYY-MM-DD and full ISO. Returns positive when `b` is
 * after `a`. Returns 0 for invalid inputs (defensive).
 */
function daysBetween(a: string, b: string): number {
  const ma = Date.parse(a);
  const mb = Date.parse(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

export function computeTimeliness(
  inputs: DataReliabilityInputs,
): ComponentResult {
  const expected = inputs.expected_instances ?? [];
  const defaultGrace = Math.max(0, inputs.grace_days);

  // ─────────────────────────────────────────────────────────────────────
  // DEV-851 per-instance path: matches DataReliabilityTab matrix exactly.
  // ─────────────────────────────────────────────────────────────────────
  if (expected.length > 0) {
    const reported = expected.filter(
      (i) => i.latest_reporting_date && i.latest_created_at,
    );
    if (reported.length === 0) {
      return {
        key: 'timeliness',
        score: null,
        applicable: false,
        reason: 'No KPI instances have submitted data yet',
        explain: 'No data submitted yet.',
      };
    }

    let onTime = 0;
    let totalLagPastGrace = 0;
    for (const inst of reported) {
      const grace = Number.isFinite(inst.grace_days)
        ? Math.max(0, inst.grace_days as number)
        : defaultGrace;
      const lag = daysBetween(
        inst.latest_reporting_date as string,
        inst.latest_created_at as string,
      );
      if (lag <= grace) {
        onTime += 1;
      } else {
        totalLagPastGrace += lag - grace;
      }
    }

    const score = Math.round((onTime / reported.length) * 100);
    const lateCount = reported.length - onTime;
    const explain =
      lateCount === 0
        ? `All ${reported.length} reporting KPI${reported.length === 1 ? '' : 's'} submitted within grace.`
        : `${onTime} of ${reported.length} on time; ${lateCount} late (avg ${Math.round(totalLagPastGrace / lateCount)}d past grace).`;

    return {
      key: 'timeliness',
      score,
      applicable: true,
      explain,
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // Legacy per-entry path: kept for existing tests and portfolio-level
  // callers that don't yet supply expected_instances.
  // ─────────────────────────────────────────────────────────────────────
  const entries = inputs.progress_entries;
  if (entries.length === 0) {
    return {
      key: 'timeliness',
      score: null,
      applicable: false,
      reason: 'No entries submitted yet — no timeliness to measure',
      explain: 'No data submitted yet.',
    };
  }

  let onTime = 0;
  let lateDays = 0;
  for (const e of entries) {
    const lag = daysBetween(e.reporting_date, e.created_at);
    if (lag <= defaultGrace) {
      onTime += 1;
    } else {
      lateDays += lag - defaultGrace;
    }
  }

  const score = Math.round((onTime / entries.length) * 100);
  const lateCount = entries.length - onTime;
  const explain =
    lateCount === 0
      ? `All ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} submitted within grace window (${defaultGrace}d).`
      : `${onTime} of ${entries.length} entries on time (${lateCount} late, average ${Math.round(lateDays / lateCount)}d past grace).`;

  return {
    key: 'timeliness',
    score,
    applicable: true,
    explain,
  };
}
